import { Button } from "@/components/ui/button";
import { ArrowRight, Stethoscope, Wrench } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const translations = {
  de: {
    badge: "EAP Skills Academy",
    title: "Skills für Ihre Praxis & Ihr Labor",
    subtitle: "Wählen Sie Ihren Bereich und entdecken Sie passgenaue Schulungen, Tutorials und Workflows auf skills.eap-abutments.com.",
    za: "Für Zahnärzte",
    zaDesc: "Chirurgische Protokolle, Indikationen und prothetische Planung – kompakt erklärt.",
    zt: "Für Zahntechniker",
    ztDesc: "Digitale Workflows, Materialkunde und Verarbeitungs-Tutorials Schritt für Schritt.",
    cta: "Skills entdecken",
  },
  en: {
    badge: "EAP Skills Academy",
    title: "Skills for your practice & your lab",
    subtitle: "Choose your area and discover tailored training, tutorials and workflows on skills.eap-abutments.com.",
    za: "For Dentists",
    zaDesc: "Surgical protocols, indications and prosthetic planning – clearly explained.",
    zt: "For Dental Technicians",
    ztDesc: "Digital workflows, materials science and processing tutorials step by step.",
    cta: "Discover skills",
  },
  fr: {
    badge: "EAP Skills Academy",
    title: "Skills pour votre cabinet & votre laboratoire",
    subtitle: "Choisissez votre domaine et découvrez des formations, tutoriels et workflows sur skills.eap-abutments.com.",
    za: "Pour les dentistes",
    zaDesc: "Protocoles chirurgicaux, indications et planification prothétique – clairement expliqués.",
    zt: "Pour les prothésistes",
    ztDesc: "Workflows numériques, matériaux et tutoriels de traitement étape par étape.",
    cta: "Découvrir les skills",
  },
  nl: {
    badge: "EAP Skills Academy",
    title: "Skills voor uw praktijk & uw lab",
    subtitle: "Kies uw vakgebied en ontdek trainingen, tutorials en workflows op skills.eap-abutments.com.",
    za: "Voor tandartsen",
    zaDesc: "Chirurgische protocollen, indicaties en prothetische planning – helder uitgelegd.",
    zt: "Voor tandtechnici",
    ztDesc: "Digitale workflows, materiaalkunde en verwerkings­tutorials stap voor stap.",
    cta: "Skills ontdekken",
  },
  it: {
    badge: "EAP Skills Academy",
    title: "Skills per il tuo studio e il tuo laboratorio",
    subtitle: "Scegli la tua area e scopri formazione, tutorial e workflow su skills.eap-abutments.com.",
    za: "Per dentisti",
    zaDesc: "Protocolli chirurgici, indicazioni e pianificazione protesica – spiegati chiaramente.",
    zt: "Per odontotecnici",
    ztDesc: "Workflow digitali, scienza dei materiali e tutorial di lavorazione passo passo.",
    cta: "Scopri gli skills",
  },
  es: {
    badge: "EAP Skills Academy",
    title: "Skills para su consulta y su laboratorio",
    subtitle: "Elija su área y descubra formación, tutoriales y workflows en skills.eap-abutments.com.",
    za: "Para dentistas",
    zaDesc: "Protocolos quirúrgicos, indicaciones y planificación protésica – explicados con claridad.",
    zt: "Para protésicos dentales",
    ztDesc: "Workflows digitales, ciencia de materiales y tutoriales de procesamiento paso a paso.",
    cta: "Descubrir skills",
  },
};

export const SkillsSection = () => {
  const { language } = useLanguage();
  const t = translations[language] ?? translations.de;

  const cards = [
    {
      icon: Stethoscope,
      title: t.za,
      desc: t.zaDesc,
      href: "https://skills.eap-abutments.com/?role=za",
    },
    {
      icon: Wrench,
      title: t.zt,
      desc: t.ztDesc,
      href: "https://skills.eap-abutments.com/?role=zt",
    },
  ];

  return (
    <section id="skills" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12 animate-fade-up">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            {t.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {cards.map(({ icon: Icon, title, desc, href }) => (
            <a
              key={title}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-8 rounded-2xl border border-border bg-card hover:border-primary hover:shadow-glow transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">{title}</h3>
              <p className="text-muted-foreground mb-6">{desc}</p>
              <Button
                variant="ghost"
                className="px-0 text-primary hover:text-primary hover:bg-transparent group/btn"
              >
                {t.cta}
                <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
